import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { catchError, share, tap, first, map, mergeMap } from 'rxjs/operators';
import { Cart } from '@core/models/cart.model';
import { API_LIST } from '@core/services/web-api/api-list';
import { PaymentSubmission } from '@core/models/payment-submission.model';

@Injectable({
  providedIn: 'root'
})
export class EmptyCartService {
  errorSubject = new Subject();
  cart: Cart | null;

  constructor(private httpClient: HttpClient) {}

  createCart(): Observable<Cart> {
    return this.httpClient.post<Cart>(API_LIST.postCart(), {}).pipe(
      tap((cartData: Cart) => {
        this.cart = cartData;
      })
    );
  }

  refreshCart(): Observable<Cart> {
    return this.httpClient.get<Cart>(API_LIST.getCart(this.cart.id, true)).pipe(
      tap((cartData: Cart) => {
        this.cart = cartData;
      })
    );
  }

  addDrawItem(drawId: number, quantity: number): Observable<Cart> {
    if (!this.cart && !this.cart.id) {
      return of(null);
    }

    let data: object = {
      type: 'CompetitionType',
      typeId1: drawId,
      quantity: quantity
    };

    return this.httpClient
      .post<Cart>(API_LIST.postCartItem(this.cart.id, true), data)
      .pipe(
        tap((cartData: Cart) => {
          this.cart = cartData;
        })
      );
  }

  applyCoupon(couponCode: string): Observable<Cart> {
    if (!this.cart) {
      return of(null);
    }
    let data = {
      code: couponCode
    };

    return this.httpClient
      .post(API_LIST.postCartCoupon(this.cart.id), data)
      .pipe(
        tap((cartData: Cart) => {
          this.cart = cartData;
        })
      );
  }

  deleteCoupon(couponCode: string): Observable<Cart> {
    if (!this.cart) {
      return of(null);
    }

    return this.httpClient
      .delete(API_LIST.deleteCartCoupon(this.cart.id, couponCode))
      .pipe(
        tap((cartData: Cart) => {
          this.cart = cartData;
        })
      );
  }

  deleteItem(itemId: any) {
    if (!this.cart.id) {
      return of(null);
    }
    if (!this.cart.items && this.cart.items.length === 0) {
      return of(null);
    }

    return this.httpClient
      .delete(API_LIST.deleteCartItem(this.cart.id, itemId))
      .pipe(
        tap((cartData: Cart) => {
          this.cart = cartData;
        })
      );
  }

  patchItem(itemId: any, quantity: number) {
    if (!this.cart.id) {
      return of(null);
    }

    if (
      !this.cart.items &&
      (this.cart.items.length === 0 ||
        this.cart.items.filter(itemId => itemId === itemId).length === 0)
    ) {
      return of(null);
    }

    return this.httpClient
      .patch(API_LIST.patchCartItem(this.cart.id, itemId, true), { quantity })
      .pipe(
        tap((cartData: Cart) => {
          this.cart = cartData;
        })
      );
  }

  postCartPayment(
    cartId: string,
    amount: string,
    submissionData: PaymentSubmission,
    typeOfBasket: 'instant' | 'buy-now' | 'regular' = 'regular'
  ) {
    return this.httpClient.post<{
      gateway_url: string;
      complete_url: string;
    }>(API_LIST.postCartPayment(cartId, amount, typeOfBasket), submissionData);
  }
}
